import { Fragment } from "react";
import { Transition, Dialog } from "@headlessui/react";
import { cn } from "../../../util/cn";
import { createPortal } from "react-dom";
import CrossSVG from "../icons/CrossSVG";

function DialogWrapper({
  children,
  title,
  subtitle,
  size = "max-w-md",
  onClose,
  titleColor = "text-[#183852]",
  titlebgColor = "",
  displayCloseIcon = false,
  displayHeaderHorizontalLine = true,
  panelClassName = "",
  contentClassName = "",
  scrollAtEdge = false,
  closeAtEdge = false,
}: {
  children: JSX.Element;
  title: string;
  subtitle?: string;
  size?: string;
  titleColor?: string;
  titlebgColor?: string;
  displayCloseIcon?: boolean;
  displayHeaderHorizontalLine?: boolean;
  panelClassName?: string;
  contentClassName?: string;
  /** When true, scrollbar sits on the modal's right edge; padding applies inside the scroll area. */
  scrollAtEdge?: boolean;
  /** When true, close icon sits near the modal corner instead of inside content padding. */
  closeAtEdge?: boolean;
  onClose: () => void;
}) {
  const showHeader = Boolean(title) || displayCloseIcon;
  const hasTitle = Boolean(title) && title !== "&nbsp;";
  const showInlineClose = displayCloseIcon && !closeAtEdge;

  return createPortal(
    <>
      <Transition appear={true} show={true}>
        <Dialog
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={onClose}
        >
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-[#15415E]/40 backdrop-blur-[2px]" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div
                className={cn(
                  "relative z-10 overflow-hidden m-0 w-full max-h-[calc(100vh-2rem)] flex flex-col text-left bg-white rounded-[22px] shadow-[0_20px_60px_-15px_rgba(21,65,94,0.25)] transition-all transform",
                  !scrollAtEdge && "p-5 sm:p-6",
                  size,
                  panelClassName,
                )}
              >
                {displayCloseIcon && closeAtEdge && (
                  <button
                    type="button"
                    aria-label="Close"
                    className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20 w-10 h-10 shrink-0 rounded-full bg-[#F8F9FA] text-[#183852] focus:outline-none hover:bg-[#E8EEF2] active:bg-[#DCE5EB] transition-colors duration-200 flex flex-row justify-center items-center"
                    onClick={() => onClose()}
                  >
                    <CrossSVG />
                  </button>
                )}

                {showHeader && (hasTitle || showInlineClose) && (
                  <div
                    className={cn(
                      "shrink-0",
                      scrollAtEdge &&
                        cn("pt-8 sm:pt-10", contentClassName || "px-5 sm:px-6"),
                    )}
                  >
                    <Dialog.Title
                      className={cn(
                        "flex flex-row justify-between items-start gap-4 mb-1 font-[Gilroy-Bold] font-[400] text-[20px] leading-[24.76px]",
                        !title && "justify-end mb-0",
                        titleColor,
                        titlebgColor,
                      )}
                    >
                      {(title || subtitle) && (
                        <div className="flex flex-col gap-1 pr-2 min-w-0">
                          {hasTitle && <span>{title}</span>}
                          {subtitle && (
                            <p className="font-[Gilroy-Medium] font-[400] text-[14px] leading-[20px] text-[#7A9099]">
                              {subtitle}
                            </p>
                          )}
                        </div>
                      )}
                      {showInlineClose && (
                        <button
                          type="button"
                          aria-label="Close"
                          className="w-10 h-10 shrink-0 rounded-full bg-[#F8F9FA] text-[#183852] focus:outline-none hover:bg-[#E8EEF2] active:bg-[#DCE5EB] transition-colors duration-200 flex flex-row justify-center items-center"
                          onClick={() => onClose()}
                        >
                          <CrossSVG />
                        </button>
                      )}
                    </Dialog.Title>
                    {displayHeaderHorizontalLine && hasTitle && (
                      <hr className="mt-4 border-[#E8EEF2]" />
                    )}
                  </div>
                )}

                <div
                  className={cn(
                    "overflow-y-auto min-h-0 flex-1 overscroll-contain",
                    !scrollAtEdge &&
                      (showHeader && hasTitle ? "pt-4" : "pt-0"),
                  )}
                >
                  {scrollAtEdge ? (
                    <div
                      className={cn(
                        "pb-8 sm:pb-10",
                        showHeader && hasTitle
                          ? "pt-4"
                          : closeAtEdge
                            ? "pt-12 sm:pt-14"
                            : showHeader
                              ? "pt-2 sm:pt-3"
                              : "pt-8 sm:pt-10",
                        contentClassName || "px-5 sm:px-6",
                      )}
                    >
                      {children}
                    </div>
                  ) : (
                    children
                  )}
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>,
    document.body,
  );
}

export default DialogWrapper;
